import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../../assets/logo.png';

const CONTACTS = [
  { initials: 'JC', name: 'James Chen', role: 'Owner' },
  { initials: 'ML', name: 'Maria Lopez', role: 'General Manager' },
  { initials: 'RK', name: 'Rachel Kim', role: 'Operations Director' },
];

export default function Hero() {
  const [shown, setShown] = useState(0);

  useEffect(() => {
    const run = () => {
      setShown(0);
      setTimeout(() => setShown(1), 800);
      setTimeout(() => setShown(2), 1400);
      setTimeout(() => setShown(3), 2000);
    };
    run();
    const id = setInterval(() => { setShown(0); setTimeout(run, 400); }, 7000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative min-h-screen bg-[#060A12] flex flex-col overflow-hidden">
      {/* dot grid */}
      <div className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: 'radial-gradient(rgba(251,213,67,0.06) 1.5px, transparent 1.5px)', backgroundSize: '28px 28px' }}
      />
      {/* glows */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full bg-[#1B3A8A]/25 blur-[160px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-500/10 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl w-full px-6 lg:px-8 pt-32 pb-16 flex-1 flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-12 xl:gap-20 items-center w-full">

          {/* LEFT */}
          <div>
            <div className="flex lg:hidden mb-8">
              <img src={logo} alt="LocalContact" className="h-7 w-auto brightness-0 invert" />
            </div>

            {/* live badge */}
            <div className="inline-flex items-center gap-2.5 rounded-full border border-brand-500/25 bg-brand-500/8 px-4 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
              <span className="text-brand-400 text-[11px] font-black uppercase tracking-[0.14em]">Live search</span>
            </div>

            {/* headline — yellow box highlight on "decision maker" */}
            <h1 className="font-black tracking-[-0.035em] text-white leading-[1.04] mb-7">
              <span className="block text-[3rem] sm:text-[4.5rem] lg:text-[5.5rem]">Find the</span>
              <span className="block">
                <span
                  className="inline-block text-[3rem] sm:text-[4.5rem] lg:text-[5.5rem] bg-brand-500 text-slate-900 px-3 sm:px-5 leading-[1.1] rounded-xl sm:rounded-2xl"
                  style={{ transform: 'rotate(-1deg)', display: 'inline-block' }}
                >
                  decision maker
                </span>
              </span>
              <span className="block text-[3rem] sm:text-[4.5rem] lg:text-[5.5rem]">behind any</span>
              <span className="block text-[3rem] sm:text-[4.5rem] lg:text-[5.5rem] text-slate-500">local business.</span>
            </h1>

            <p className="text-slate-400 text-[16px] sm:text-[17px] leading-relaxed max-w-[480px] mb-10">
              Enter a business name and address. Get the real people running it, name and title for every contact found.{' '}
              <span className="text-white font-semibold">One credit per company, not per contact.</span>
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <Link
                to="/register"
                className="inline-flex items-center gap-2.5 bg-brand-500 text-slate-900 font-black text-[15px] px-9 py-4 rounded-2xl hover:bg-brand-400 hover:scale-[1.02] active:scale-[0.98] transition-all duration-150 shadow-2xl shadow-brand-500/25"
              >
                Start for free
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 text-slate-300 font-bold text-[15px] px-9 py-4 rounded-2xl border border-slate-700 hover:border-brand-500/60 hover:text-white transition-all duration-200"
              >
                See how it works
              </a>
            </div>

            {/* floating yellow stat boxes */}
            <div className="flex flex-wrap gap-3">
              <div className="bg-brand-500 rounded-xl px-4 py-3 flex items-center gap-3">
                <span className="text-[2rem] font-black text-slate-900 leading-none">1</span>
                <div>
                  <div className="text-[11px] font-black text-slate-900 uppercase tracking-wide">credit</div>
                  <div className="text-[11px] text-slate-800">per company</div>
                </div>
              </div>
              <div className="bg-white rounded-xl px-4 py-3 flex items-center gap-3">
                <span className="text-[2rem] font-black text-slate-900 leading-none">3K+</span>
                <div>
                  <div className="text-[11px] font-black text-slate-700 uppercase tracking-wide">business</div>
                  <div className="text-[11px] text-slate-500">categories</div>
                </div>
              </div>
              <div className="bg-[#161B22] border border-slate-700/60 rounded-xl px-4 py-3 flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full bg-brand-400 animate-pulse flex-shrink-0" />
                <div>
                  <div className="text-[11px] font-black text-white uppercase tracking-wide">Live</div>
                  <div className="text-[11px] text-slate-400">real-time results</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: search demo */}
          <div className="relative">
            {/* glow */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-80 h-80 bg-brand-500/10 rounded-full blur-[80px]" />
            </div>

            {/* yellow accent bar */}
         
          

            <div className="relative bg-[#0D1117] rounded-3xl border border-slate-700/60 shadow-2xl overflow-hidden">
              {/* top yellow bar */}
              <div className="h-1 bg-brand-500 w-full" />

              <div className="p-6">
                {/* card header */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2.5">
                    <img src={logo} alt="LocalContact" className="h-5 w-auto brightness-0 invert opacity-60" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Search</span>
                  </div>
                  <span className="flex items-center gap-1.5 text-[10px] font-bold text-brand-400 bg-brand-500/10 border border-brand-500/25 rounded-full px-3 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
                    Live
                  </span>
                </div>

                {/* inputs */}
                <div className="space-y-3 mb-5">
                  {[
                    { label: 'Business', val: 'Lucky Dragon Restaurant' },
                    { label: 'Address', val: '123 Main St, Austin TX' },
                    { label: 'Titles', val: 'Owner, GM, Operations Manager' },
                  ].map(({ label, val }) => (
                    <div key={label}>
                      <div className="text-[9.5px] text-slate-600 font-black mb-1.5 uppercase tracking-widest">{label}</div>
                      <div className="bg-[#161B22] rounded-xl px-4 py-3 text-[13px] text-slate-200 font-medium border border-slate-800">
                        {val}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-brand-500 rounded-xl px-4 py-3 text-center text-[13px] font-black text-slate-900 mb-4">
                  Search · 1 credit
                </div>

                {/* separator */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-slate-800" />
                  <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Results</span>
                  <div className="flex-1 h-px bg-slate-800" />
                </div>

                {/* result cards */}
                <div className="space-y-2">
                  {CONTACTS.map((c, i) => (
                    <div
                      key={i}
                      className="bg-[#161B22] border border-slate-800 rounded-xl px-4 py-3 flex items-center gap-3"
                      style={{
                        opacity: shown > i ? 1 : 0,
                        transform: shown > i ? 'translateX(0)' : 'translateX(-12px)',
                        transition: 'opacity 0.4s ease, transform 0.4s ease',
                      }}
                    >
                      <div className="w-8 h-8 rounded-lg bg-brand-500/20 border border-brand-500/30 flex items-center justify-center text-[10px] font-black text-brand-400 flex-shrink-0">
                        {c.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-bold text-white">{c.name}</div>
                        <div className="text-[11px] text-slate-500">{c.role}</div>
                      </div>
                      <div className="w-5 h-5 rounded-full bg-brand-500/20 border border-brand-500/40 flex items-center justify-center flex-shrink-0">
                        <svg className="w-2.5 h-2.5 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  ))}

                  {/* +3 more */}
                  <div
                    className="bg-brand-500/10 border border-brand-500/20 rounded-xl px-4 py-2.5 flex items-center justify-between"
                    style={{
                      opacity: shown >= CONTACTS.length ? 1 : 0,
                      transition: 'opacity 0.4s ease 0.2s',
                    }}
                  >
                    <span className="text-[12px] font-bold text-brand-400">+3 more contacts found</span>
                    <span className="text-[11px] text-brand-600 font-semibold">Export CSV →</span>
                  </div>
                </div>
              </div>
            </div>

            {/* floating badge — total this search */}
            <div
              className="absolute -bottom-14 -right-4 bg-white rounded-2xl px-4 py-3 shadow-2xl border border-slate-100"
              style={{ animation: 'floatY 4s ease-in-out infinite' }}
            >
              <div className="text-[22px] font-black text-slate-900 leading-none mb-0.5">$0.004</div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">per company at scale</div>
            </div>
          </div>

        </div>
      </div>

      {/* bottom wave into next section */}
      <div className="relative h-16 pointer-events-none">
        <svg viewBox="0 0 1440 64" className="absolute bottom-0 w-full" preserveAspectRatio="none" fill="white">
          <path d="M0,64 C360,0 1080,0 1440,64 L1440,64 L0,64 Z" />
        </svg>
      </div>

      <style>{`
        @keyframes floatY {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </section>
  );
}
